import React, { useEffect } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { NavigationBlocker } from "../components/common/NavigationBlocker";
import {
  useSecurityEditorStore,
  selectSecurityIsDirty,
} from "../store/useSecurityEditorStore";
import { useShopifySaveBar } from "../hooks/useShopifySaveBar";
import { SecurityTabBar } from "./security/SecurityTabBar";

// ---------- Component ----------

const SecuritySettingsEditorPage: React.FC = () => {
  const loadFromBackend = useSecurityEditorStore((s) => s.loadFromBackend);
  const updateField = useSecurityEditorStore((s) => s.updateField);
  const save = useSecurityEditorStore((s) => s.save);
  const discard = useSecurityEditorStore((s) => s.discardChanges);
  const isDirty = useSecurityEditorStore(selectSecurityIsDirty);
  const isSaving = useSecurityEditorStore((s) => s.isSaving);
  const isLoading = useSecurityEditorStore((s) => s.isLoading);
  const error = useSecurityEditorStore((s) => s.error);

  const velocityEnabled = useSecurityEditorStore((s) => s.velocityEnabled);
  const velocityLimit = useSecurityEditorStore((s) => s.velocityLimit);
  const velocityWindowHours = useSecurityEditorStore(
    (s) => s.velocityWindowHours
  );
  const maxItemsEnabled = useSecurityEditorStore((s) => s.maxItemsEnabled);
  const maxItemsPerOrder = useSecurityEditorStore((s) => s.maxItemsPerOrder);
  const blockedEmails = useSecurityEditorStore((s) => s.blockedEmails);
  const blockedPhones = useSecurityEditorStore((s) => s.blockedPhones);
  const blockedIps = useSecurityEditorStore((s) => s.blockedIps);

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  useShopifySaveBar({ isDirty, isSaving, onSave: save, onDiscard: discard });

  if (isLoading) {
    return (
      <div>
        <s-page heading="Bloqueo de usuario">
          <SecurityTabBar />
          <s-box padding="large-400">
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando configuración...</s-text>
            </s-stack>
          </s-box>
        </s-page>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <s-page heading="Bloqueo de usuario">
          <SecurityTabBar />
          <s-banner tone="critical">
            <s-text>Error al cargar: {error}</s-text>
          </s-banner>
        </s-page>
      </div>
    );
  }

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading="Bloqueo de usuario">
        <SecurityTabBar />
        <h2>
          <strong>
            Si desea bloquear a usuarios específicos para que no realicen
            pedidos en el formulario, ingrese su dirección IP aquí.
          </strong>
        </h2>
        <s-box padding="base" />
        <s-section>
          <s-stack direction="block" gap="large-200">
            <s-checkbox
              label="Limita pedidos realizados por el mismo cliente en X horas"
              details="Para determinar el cliente utilizamos una combinación de dirección IP, correo electrónico o número de teléfono."
              checked={velocityEnabled}
              onChange={(e: CallbackEvent<"s-checkbox">) =>
                updateField("velocityEnabled", e.currentTarget.checked)
              }
            />
            {velocityEnabled && (
              <s-stack direction="inline" gap="base" alignItems="center">
                <s-text>Permitir solo</s-text>
                <s-box inlineSize="auto" maxInlineSize="80px">
                  <s-number-field
                    min={1}
                    value={String(velocityLimit)}
                    onInput={(e: CallbackEvent<"s-number-field">) =>
                      updateField(
                        "velocityLimit",
                        Math.max(1, parseInt(e.currentTarget.value, 10) || 1)
                      )
                    }
                  />
                </s-box>
                <s-text>pedido(s) desde la misma dirección IP en</s-text>
                <s-box inlineSize="auto" maxInlineSize="140px">
                  <s-select
                    value={String(velocityWindowHours)}
                    onChange={(e: CallbackEvent<"s-select">) =>
                      updateField(
                        "velocityWindowHours",
                        parseInt(e.currentTarget.value, 10)
                      )
                    }
                  >
                    <s-option value="1">1 hour</s-option>
                    <s-option value="3">3 hours</s-option>
                    <s-option value="6">6 hours</s-option>
                    <s-option value="12">12 hours</s-option>
                    <s-option value="24">24 hours</s-option>
                    <s-option value="48">48 hours</s-option>
                    <s-option value="72">72 hours</s-option>
                  </s-select>
                </s-box>
              </s-stack>
            )}

            <s-divider />

            <s-checkbox
              label="Bloquear pedidos si contienen más de X cantidad de productos"
              checked={maxItemsEnabled}
              onChange={(e: CallbackEvent<"s-checkbox">) =>
                updateField("maxItemsEnabled", e.currentTarget.checked)
              }
            />
            {maxItemsEnabled && (
              <s-stack direction="inline" gap="base" alignItems="center">
                <s-text>
                  Los pedidos se bloquearán si la cantidad de productos en el
                  pedido es superior a:
                </s-text>
                <s-box inlineSize="auto" maxInlineSize="80px">
                  <s-number-field
                    min={1}
                    value={String(maxItemsPerOrder)}
                    onInput={(e: CallbackEvent<"s-number-field">) =>
                      updateField(
                        "maxItemsPerOrder",
                        Math.max(1, parseInt(e.currentTarget.value, 10) || 1)
                      )
                    }
                  />
                </s-box>
              </s-stack>
            )}

            <s-divider />

            <s-grid gridTemplateColumns="1fr 1fr" gap="base">
              <s-stack direction="block" gap="small-200">
                <s-text>
                  Correos electrónicos a <strong>bloquear</strong> (separados
                  por una nueva línea)
                </s-text>
                <s-text-area
                  placeholder={`johnsmith@gmail.com\narianapiccola@outlook.com\ndiego@gmail.com`}
                  rows={4}
                  value={blockedEmails}
                  onInput={(e: CallbackEvent<"s-text-area">) =>
                    updateField("blockedEmails", e.currentTarget.value)
                  }
                />
              </s-stack>
              <s-stack direction="block" gap="small-200">
                <s-text>
                  Números de teléfono a <strong>bloquear</strong> (separados por
                  una nueva línea)
                </s-text>
                <s-text-area
                  placeholder={`6721956382\n9683121183\n41822744`}
                  rows={4}
                  value={blockedPhones}
                  onInput={(e: CallbackEvent<"s-text-area">) =>
                    updateField("blockedPhones", e.currentTarget.value)
                  }
                />
                <s-text color="subdued">
                  Introduzca los números de teléfono sin código de país.
                </s-text>
              </s-stack>
            </s-grid>

            <s-divider />

            <s-stack direction="block" gap="small-200">
              <s-text>
                Direcciones IP a <strong>bloquear</strong> (separadas por una
                nueva línea)
              </s-text>
              <s-text-area
                placeholder={`1.56.78.9\n86.95.222.1\n35.7.23.11`}
                rows={4}
                value={blockedIps}
                onInput={(e: CallbackEvent<"s-text-area">) =>
                  updateField("blockedIps", e.currentTarget.value)
                }
              />
            </s-stack>
          </s-stack>
        </s-section>
      </s-page>
    </div>
  );
};

export default SecuritySettingsEditorPage;
